// Main Feed Container Component
// TikTok-inspired social feed with mixed content support

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FeedContainer as StyledFeedContainer,
  FeedNavigation,
  FeedTabs,
  FeedTab,
  TabIcon,
  FeedContent,
  FeedItemContainer,
  LoadingContainer,
  LoadingSpinner,
  LoadingText,
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateDescription,
  MobileOptimizedContainer,
} from "../../../styles/SocialFeed/FeedContainer.styles";
import { signalsService, SignalSurface } from "../../../services/SocialFeed/signalsService";
import { FeedItem, FeedTab as FeedTabType, FeedInteraction } from "../../../types/socialFeed";
import { useSocialFeed, useSwipeMode, useFeedInteractions } from "../../../hooks";
import { useTabSwipe } from "../../../hooks/socialFeed/useTabSwipe";
import {
  prefetchFeeds,
  whenIdle,
  PrefetchableTab,
} from "../../../services/SocialFeed/feedPrefetch";
import { FeedItemRenderer } from "../FeedItems/FeedItemRenderer";
import { logger } from "../../../utils/logger";
import { SwipeModeOverlay } from "../SwipeMode/SwipeModeOverlay";
import { MatchesTabContainer } from "../MatchesTab/MatchesTabContainer";
import { NearbyTabContainer } from "../NearbyTab/NearbyTabContainer";
import { ExploreTabContainer } from "../ExploreTab/ExploreTabContainer";
import { SingleCardFeedContainer } from "../SingleCardFeed/SingleCardFeedContainer";
import { StoriesTabContainer } from "../StoriesTab/StoriesTabContainer";
import { FEED_TAB_ICONS, FEED_TAB_LABELS } from "../../../types/socialFeed";

interface FeedContainerProps {
  userId: string;
  initialFilters?: any;
  onItemInteraction?: (interaction: FeedInteraction) => void;
  onSwipeModeToggle?: (enabled: boolean) => void;
  className?: string;
}

// Feed tabs that map onto a signal surface. "heart-games" is intentionally
// absent: it is a tab, not a feed surface, and the signal log only recognises
// the surfaces listed in migration 045.
const TAB_SURFACE: Partial<Record<string, SignalSurface>> = {
  matches: 'matches',
  nearby: 'nearby',
  explore: 'explore',
  stories: 'moments',
};

export const FeedContainer: React.FC<FeedContainerProps> = ({
  userId,
  initialFilters,
  onItemInteraction,
  onSwipeModeToggle,
  className,
}) => {
  const navigate = useNavigate();
  const feedRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<"up" | "down">("down");

  // Hooks
  const {
    feedItems,
    isLoading,
    hasMore,
    filters,
    activeTab,
    setFilters,
    setActiveTab,
    loadFeedItems,
    handleInteraction,
    refreshFeed,
  } = useSocialFeed(userId);

  // Debug logging removed for cleaner console

  const {
    isSwipeModeActive,
    currentSwipeItem,
    openSwipeMode,
    closeSwipeMode,
    handleSwipe,
  } = useSwipeMode();

  const {
    handleLike,
    handleComment,
    handleShare,
    handleView,
    isInteracting,
  } = useFeedInteractions();

  // Tab management
  const [unreadCounts, setUnreadCounts] = useState<Record<FeedTabType, number>>({
    stories: 0,
    "heart-games": 0,
    matches: 0,
    explore: 0,
    nearby: 0,
  });

  // Scroll handling
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const scrollPercentage = scrollTop / (scrollHeight - clientHeight);
    
    // Update current index based on scroll position
    const newIndex = Math.floor(scrollPercentage * feedItems.length);
    setCurrentIndex(newIndex);

    // Detect scroll direction
    const newScrollDirection = scrollTop > (feedRef.current?.scrollTop || 0) ? "down" : "up";
    setScrollDirection(newScrollDirection);
    setIsScrolling(true);

    // Load more items when near bottom
    if (scrollPercentage > 0.8 && hasMore && !isLoading) {
      loadFeedItems();
    }
  }, [feedItems.length, hasMore, isLoading, loadFeedItems]);

  const handleScrollEnd = useCallback(() => {
    setIsScrolling(false);
  }, []);

  // Interaction handlers
  const handleItemInteraction = useCallback(async (interaction: FeedInteraction) => {
    try {
      await handleInteraction(interaction);
      onItemInteraction?.(interaction);
    } catch (error) {
      console.error("Failed to handle interaction:", error);
    }
  }, [handleInteraction, onItemInteraction]);

  const handleUserClick = useCallback((userId: string) => {
    navigate(`/profile/view/${userId}`);
  }, [navigate]);

  const handleSwipeModeTrigger = useCallback((item: FeedItem) => {
    openSwipeMode(item);
    onSwipeModeToggle?.(true);
  }, [openSwipeMode, onSwipeModeToggle]);

  const handleSwipeModeClose = useCallback(() => {
    closeSwipeMode();
    onSwipeModeToggle?.(false);
  }, [closeSwipeMode, onSwipeModeToggle]);

  // Tab change handler
  const handleTabChange = useCallback((tab: FeedTabType) => {
    // Which surfaces a user actually reaches for is part of the engagement
    // picture, even though it says nothing about any one profile.
    signalsService.track({
      signalType: 'feed_tab_switch',
      // Only tabs that correspond to a real surface carry one — "heart-games"
      // is a tab but not a feed surface, and sending it would be rejected.
      surface: TAB_SURFACE[tab],
      metadata: { from: activeTab, to: tab },
    });
    setActiveTab(tab);
    setCurrentIndex(0);
    if (feedRef.current) {
      feedRef.current.scrollTop = 0;
    }
    // No reload here. This used to call loadFeedItems(true) on every tab
    // change, which refetched the Matches feed even when moving to Explore or
    // Nearby, and threw away the copy that was about to be reused. The hook
    // loads what it needs when Matches is the tab being shown.
  }, [activeTab, setActiveTab]);

  // The tabs as they appear, left to right. The bar renders from this and the
  // swipe gesture steps through it, so the two can never disagree about which
  // tab is "next".
  const visibleTabs = useMemo(
    () =>
      (Object.keys(FEED_TAB_LABELS) as FeedTabType[])
        .filter((tab) => tab !== 'heart-games')
        .reverse(),
    []
  );

  /**
   * Warm the tabs the viewer has not opened.
   *
   * Swiping between tabs meant watching a spinner every time, because each tab
   * unmounts and refetches from nothing. Now they are loaded before the swipe
   * happens and rendered from the cache when it does.
   *
   * The cost is kept off the critical path in three ways: it waits for an idle
   * callback, so it cannot run while the visible tab is still laying out and
   * painting; it fetches one tab at a time rather than racing the active feed
   * for connections; and it skips anything already cached. The nearest tabs go
   * first, since a swipe reaches those next.
   */
  useEffect(() => {
    if (!userId) return;

    const others = visibleTabs.filter(
      (tab): tab is PrefetchableTab => tab !== activeTab && tab !== 'heart-games'
    );
    const current = visibleTabs.indexOf(activeTab);
    const byNearness = [...others].sort(
      (a, b) =>
        Math.abs(visibleTabs.indexOf(a) - current) -
        Math.abs(visibleTabs.indexOf(b) - current)
    );

    return whenIdle(() => {
      prefetchFeeds(userId, byNearness);
    });
  }, [userId, activeTab, visibleTabs]);

  const { swipeHandlers } = useTabSwipe<FeedTabType>({
    tabs: visibleTabs,
    activeTab,
    onChange: handleTabChange,
    // Swipe Mode is a full-screen overlay with its own left/right gestures,
    // and those mean keep and pass. Changing tabs underneath it would be both
    // invisible and destructive.
    disabled: isSwipeModeActive,
  });


  // Settings handler
  const handleSettingsOpen = useCallback(() => {
    navigate("/social-feed/settings");
  }, [navigate]);

  // Refresh handler
  const handleRefresh = useCallback(() => {
    refreshFeed();
    setCurrentIndex(0);
  }, [refreshFeed]);

  // Load initial data
  useEffect(() => {
    // Set default tab to "stories" instead of "all"
    if (activeTab === "stories") {
      // Stories tab doesn't need feed items
      return;
    }
    // Load feed items for the current tab
    loadFeedItems(true);
  }, [userId, activeTab]); // Removed loadFeedItems from deps to avoid infinite loop

  // Update unread counts (mock data for now)
  useEffect(() => {
    const mockUnreadCounts = {
      stories: 0, // Stories don't have unread counts in the same way
      "heart-games": feedItems.filter(item => 
        item.type.includes("heart-games") && isNewItem(item.timestamp)
      ).length,
      matches: feedItems.filter(item => 
        item.type === "compatibility-suggestion" && isNewItem(item.timestamp)
      ).length,
      explore: feedItems.filter(item => 
        ["community-challenge", "leaderboard-update", "compatibility-suggestion"].includes(item.type) && isNewItem(item.timestamp)
      ).length,
      nearby: feedItems.filter(item => 
        (item.metadata?.location !== undefined || ["social-activity", "profile-update"].includes(item.type)) && isNewItem(item.timestamp)
      ).length,
    };
    setUnreadCounts(mockUnreadCounts);
  }, [feedItems]);

  // Helper function to check if item is new
  const isNewItem = (timestamp: Date): boolean => {
    const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
    return new Date(timestamp) > oneHourAgo;
  };

  return (
    <StyledFeedContainer className={className} {...swipeHandlers}>
      {/* Feed Navigation */}
      {/* The bar scrolls horizontally when the tabs overflow, so a drag here
          is a scroll rather than a tab change. */}
      <FeedNavigation data-no-tab-swipe>
        <FeedTabs>
          {visibleTabs.map((tab) => (
            <FeedTab
              key={tab}
              $active={activeTab === tab}
              onClick={() => handleTabChange(tab)}
            >
              <TabIcon>{FEED_TAB_ICONS[tab]}</TabIcon>
              {FEED_TAB_LABELS[tab]}
            </FeedTab>
          ))}
        </FeedTabs>
      </FeedNavigation>

      {/* Feed Content */}
      {activeTab === "stories" ? (
        // onViewProfile is deliberately not passed: the tab opens the profile
        // in a drawer over the feed. Supplying handleUserClick here would
        // navigate away instead, which is what used to interrupt the reel.
        <StoriesTabContainer
          userId={userId}
          onClose={() => handleTabChange("matches")}
        />
      ) : activeTab === "matches" ? (
        <MatchesTabContainer
          items={feedItems}
          userId={userId}
          isLoading={isLoading}
          hasMore={hasMore}
          onLoadMore={() => loadFeedItems()}
          onRefresh={() => loadFeedItems(true)}
          keepPileCount={0} // TODO: Get from keep pile state
          maxKeepPileSize={5}
        />
      ) : activeTab === "nearby" ? (
        <NearbyTabContainer userId={userId} />
      ) : activeTab === "explore" ? (
        // Same as Moments above: the tab owns the drawer, so no navigating
        // handler is passed.
        <ExploreTabContainer userId={userId} />
      ) : (
        <SingleCardFeedContainer
          items={feedItems}
          userId={userId}
          isLoading={isLoading}
          hasMore={hasMore}
          onLoadMore={() => loadFeedItems()}
          onItemInteraction={handleItemInteraction}
          onUserClick={handleUserClick}
          onSwipeModeTrigger={handleSwipeModeTrigger}
        />
      )}

      {/* Swipe Mode Overlay */}
      <SwipeModeOverlay
        isOpen={isSwipeModeActive}
        item={currentSwipeItem!}
        onClose={handleSwipeModeClose}
        onSwipe={handleSwipe}
        onProfileView={handleUserClick}
      />
    </StyledFeedContainer>
  );
};
